import React, { createContext, useContext, useReducer, useMemo } from 'react';

const initialState = {
  sessionId: null,
  status: 'idle',
  messages: [],
  wireframes: [],
  lead: null,
  error: null,
  suggestions: [],
  currentStage: 'understanding',
};

function stageForUserCount(count) {
  if (count >= 6) return 'prototyping';
  if (count >= 3) return 'exploring';
  return 'understanding';
}

function countUserMessages(messages) {
  let n = 0;
  for (const m of messages) {
    if (m && m.role === 'user') n += 1;
  }
  return n;
}

function reducer(state, action) {
  switch (action.type) {
    case 'INIT_SESSION': {
      const { sessionId, messages, wireframes, lead } = action.payload || {};
      const msgs = messages ?? [];
      return {
        ...state,
        sessionId: sessionId ?? state.sessionId,
        messages: msgs,
        wireframes: wireframes ?? [],
        lead: lead ?? null,
        error: null,
        suggestions: [],
        currentStage: stageForUserCount(countUserMessages(msgs)),
      };
    }
    case 'SEND_MESSAGE':
      return {
        ...state,
        messages: [
          ...state.messages,
          { role: 'user', content: action.payload.content },
        ],
        status: 'loading',
        suggestions: [],
      };
    case 'ADD_ASSISTANT_MESSAGE': {
      const payload = action.payload || {};
      const message = { role: 'assistant', content: payload.content ?? '' };
      if (payload.type) message.type = payload.type;
      if (payload.image) message.image = payload.image;
      if (payload.wireframeVersion != null) {
        message.wireframeVersion = payload.wireframeVersion;
      }
      const userCount = countUserMessages(state.messages);
      return {
        ...state,
        messages: [...state.messages, message],
        status: 'idle',
        currentStage: stageForUserCount(userCount),
      };
    }
    case 'SET_STATUS':
      return { ...state, status: action.payload };
    case 'SET_SUGGESTIONS':
      return {
        ...state,
        suggestions: Array.isArray(action.payload) ? action.payload : [],
      };
    case 'CLEAR_SUGGESTIONS':
      return { ...state, suggestions: [] };
    case 'SET_STAGE':
      return { ...state, currentStage: action.payload };
    case 'WIREFRAME_START':
      return { ...state, status: 'wireframe-generating', error: null };
    case 'WIREFRAME_COMPLETE':
      return {
        ...state,
        wireframes: [...state.wireframes, action.payload],
        status: 'idle',
      };
    case 'WIREFRAME_FAILED':
      return { ...state, status: 'idle', error: action.payload };
    case 'CAPTURE_LEAD':
      return { ...state, lead: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'RESET_SESSION':
      return initialState;
    default: {
      if (process.env.NODE_ENV !== 'test') {
        console.warn(`Unknown discovery action type: ${action.type}`);
      }
      return state;
    }
  }
}

const DiscoveryContext = createContext(null);

export function DiscoveryProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return (
    <DiscoveryContext.Provider value={value}>
      {children}
    </DiscoveryContext.Provider>
  );
}

export function useDiscoveryContext() {
  const ctx = useContext(DiscoveryContext);
  if (!ctx) {
    throw new Error(
      'useDiscoveryContext must be used inside a DiscoveryProvider',
    );
  }
  return ctx;
}
